import { z } from "zod";
export const CORPORATE_EMAIL_DOMAIN = "ubisafe.com.br";
export const CORPORATE_EMAIL_SUFFIX = `@${CORPORATE_EMAIL_DOMAIN}`;
export const roleSchema = z.enum(["ADMIN", "PRE_IMPLEMENTATION", "FINANCE"]);
export const userStatusSchema = z.enum(["ACTIVE", "DISABLED"]);
export const tenantClassificationSchema = z.enum(["CLIENT", "POC", "TRIAL", "IGNORED"]);
export const tenantOverrideClassificationSchema = z.enum(["POC", "TRIAL", "IGNORED"]);
export const tenantSnapshotSourceSchema = z.enum(["CONTROLLED", "MYSQL"]);
export const emailPrefixSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, "Informe o usuário")
    .max(64, "O usuário deve ter no máximo 64 caracteres")
    .regex(/^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/, "Use apenas letras minúsculas, números, ponto, hífen ou sublinhado");
export const passwordSchema = z
    .string()
    .min(10, "A senha deve ter no mínimo 10 caracteres")
    .max(128, "A senha deve ter no máximo 128 caracteres")
    .regex(/[A-Z]/, "Inclua ao menos uma letra maiúscula")
    .regex(/[a-z]/, "Inclua ao menos uma letra minúscula")
    .regex(/[0-9]/, "Inclua ao menos um número")
    .regex(/[^A-Za-z0-9]/, "Inclua ao menos um caractere especial");
export const rolesSchema = z
    .array(roleSchema)
    .min(1, "Selecione ao menos um perfil")
    .transform((roles) => Array.from(new Set(roles)))
    .refine((roles) => roles.includes("ADMIN") ||
    roles.includes("PRE_IMPLEMENTATION") ||
    roles.includes("FINANCE"), "Usuários não administradores devem possuir Pré-implantação, Financeiro ou ambos");
export const loginInputSchema = z.object({
    prefix: emailPrefixSchema,
    password: z.string().min(1, "Informe a senha").max(128),
});
export const changePasswordInputSchema = z.object({
    currentPassword: z.string().min(1, "Informe a senha atual").max(128),
    newPassword: passwordSchema,
});
export const forgotPasswordInputSchema = z.object({
    prefix: emailPrefixSchema,
});
export const resetPasswordInputSchema = z.object({
    token: z.string().min(32, "Token inválido").max(512),
    newPassword: passwordSchema,
});
export const createUserInputSchema = z.object({
    name: z.string().trim().min(2, "Informe o nome").max(120),
    prefix: emailPrefixSchema,
    roles: rolesSchema,
});
export const updateUserInputSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    roles: rolesSchema.optional(),
    status: userStatusSchema.optional(),
});
export const userViewSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    emailPrefix: z.string(),
    email: z.string().email(),
    roles: z.array(roleSchema),
    status: userStatusSchema,
    mustChangePassword: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const authSessionViewSchema = z.object({
    user: userViewSchema,
    expiresAt: z.string().datetime(),
});
export const tenantViewSchema = z.object({
    identifier: z.string().min(1).max(64),
    name: z.string().min(1).max(160),
    activePatrimonies: z.number().int().nonnegative(),
    synchronizedAt: z.string().datetime(),
    classification: tenantClassificationSchema,
    source: tenantSnapshotSourceSchema,
    hasActivePoc: z.boolean(),
});
export const tenantListViewSchema = z.object({
    tenants: z.array(tenantViewSchema),
    synchronizedAt: z.string().datetime(),
    source: tenantSnapshotSourceSchema,
});
export const updateTenantClassificationInputSchema = z.object({
    classification: tenantClassificationSchema,
});
function isValidLocalDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}
export const localDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Use uma data válida")
    .refine(isValidLocalDate, "Use uma data válida");
const requiredLeadSchema = z.string().trim().min(1, "Informe o Lead").max(160, "O Lead deve ter no máximo 160 caracteres");
const requiredCommercialOwnerSchema = z
    .string()
    .trim()
    .min(1, "Informe o Comercial responsável")
    .max(120, "O Comercial responsável deve ter no máximo 120 caracteres");
const optionalNotesSchema = z
    .string()
    .trim()
    .max(4_000, "As anotações devem ter no máximo 4.000 caracteres")
    .optional()
    .transform((value) => value || null);
export const pocCycleInputSchema = z
    .object({
    lead: requiredLeadSchema,
    startDate: localDateSchema,
    expectedEndDate: localDateSchema,
    commercialOwner: requiredCommercialOwnerSchema,
    notes: optionalNotesSchema,
})
    .superRefine((input, context) => {
    if (input.expectedEndDate < input.startDate) {
        context.addIssue({
            code: "custom",
            path: ["expectedEndDate"],
            message: "A previsão de término não pode anteceder o início",
        });
    }
});
export const pocOperationalStatusSchema = z.enum(["AVAILABLE", "IN_USE", "OVERDUE"]);
const pocCycleFieldsSchema = z.object({
    id: z.string().uuid(),
    lead: z.string(),
    startDate: localDateSchema,
    expectedEndDate: localDateSchema,
    commercialOwner: z.string(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const pocActiveCycleViewSchema = pocCycleFieldsSchema;
export const cleanupJobStatusSchema = z.enum([
    "PENDING",
    "PRECHECKING",
    "INVENTORY",
    "AWAITING_CONFIRMATION",
    "RUNNING",
    "PAUSED_ITEM_ERROR",
    "WAITING_SERVICE",
    "FAILED",
    "VERIFYING",
    "VERIFICATION_FAILED",
    "COMPLETED_WITH_PENDING",
    "COMPLETED",
]);
export const cleanupStageSchema = z.enum([
    "PRECHECK",
    "INVENTORY",
    "PARTITION_HABIT_DEVIATION_OVERRIDES",
    "OFFICE_HOURS",
    "OCCURRENCE_SCHEDULES",
    "ACCOUNT_SUSPENSIONS",
    "ACCOUNT_PAYMENT_METHODS",
    "CONTACT_MESSAGE_CONFIGURATIONS",
    "PATRIMONY_INTRUSIONS",
    "PARTITION_CONTACT_LINKS",
    "DEVICES",
    "PGMS",
    "PERIODIC_EVENT_CONTROLS",
    "PARTITIONS",
    "CENTRALS",
    "CONTACT_ACCOUNT_LINKS",
    "ACCOUNTS",
    "PATRIMONIES",
    "CONTACTS",
    "CUSTOMERS",
    "SERVICE_ORDER_AUTOMATIONS",
    "HOLIDAYS",
    "VERIFICATION",
    "COMPLETED",
]);
export const cleanupItemEntitySchema = z.enum([
    "PARTITION_HABIT_DEVIATION_OVERRIDE",
    "OFFICE_HOURS",
    "OCCURRENCE_SCHEDULE",
    "ACCOUNT_SUSPENSION",
    "ACCOUNT_PAYMENT_METHOD",
    "CONTACT_MESSAGE_CONFIGURATION",
    "PATRIMONY_INTRUSION",
    "PARTITION_CONTACT_LINK",
    "DEVICE",
    "PGM",
    "PERIODIC_EVENT_CONTROL",
    "PARTITION",
    "CENTRAL",
    "CONTACT_ACCOUNT_LINK",
    "ACCOUNT",
    "PATRIMONY",
    "CONTACT",
    "CUSTOMER",
    "SERVICE_ORDER_AUTOMATION",
    "HOLIDAY",
]);
export const cleanupStepStatusSchema = z.enum([
    "PENDING",
    "AWAITING_CONFIRMATION",
    "CONFIRMED",
    "RUNNING",
    "COMPLETED",
    "COMPLETED_WITH_PENDING",
    "SKIPPED_EMPTY",
]);
export const cleanupCompletionModeSchema = z.enum(["AUTO_VERIFIED", "MANUAL_WITH_PENDING"]);
export const cleanupJobProviderSchema = z.enum(["SIMULATOR", "REAL"]);
export const cleanupStepViewSchema = z.object({
    stage: cleanupStageSchema,
    executionOrder: z.number().int().nonnegative(),
    status: cleanupStepStatusSchema,
    totalCount: z.number().int().nonnegative(),
    completedCount: z.number().int().nonnegative(),
    ignoredCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    confirmedAt: z.string().datetime().nullable(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
});
export const cleanupFailedItemViewSchema = z.object({
    id: z.string().uuid(),
    entity: cleanupItemEntitySchema,
    externalId: z.string(),
    parentExternalId: z.string(),
    attempts: z.number().int().nonnegative(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
export const cleanupJobViewSchema = z.object({
    id: z.string().uuid(),
    provider: cleanupJobProviderSchema,
    status: cleanupJobStatusSchema,
    currentStage: cleanupStageSchema,
    attempt: z.number().int().nonnegative(),
    progressCompleted: z.number().int().nonnegative(),
    progressTotal: z.number().int().nonnegative(),
    ignoredCount: z.number().int().nonnegative(),
    errorCode: z.string().nullable(),
    completionMode: cleanupCompletionModeSchema.nullable(),
    requestedAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    manuallyCompletedAt: z.string().datetime().nullable(),
    autoAdvancePaused: z.boolean(),
    steps: z.array(cleanupStepViewSchema).optional(),
    failedItem: cleanupFailedItemViewSchema.nullable().optional(),
});
export const cleanupServiceStatusSchema = z.enum(["AVAILABLE", "UNAVAILABLE", "REQUESTED"]);
export const cleanupExecutionModeSchema = z.enum(["DISABLED", "SIMULATOR", "REAL"]);
export const cleanupServiceViewSchema = z.object({
    executionMode: cleanupExecutionModeSchema,
    status: cleanupServiceStatusSchema,
    canRequestRelease: z.boolean(),
    requestedAt: z.string().datetime().nullable(),
});
export const createCleanupJobInputSchema = z.object({
    confirmationIdentifier: z.string().min(1, "Digite o Identificador do tenant").max(64),
});
export const confirmCleanupStepInputSchema = z.object({
    expectedCount: z.number().int().nonnegative(),
});
export const setCleanupAutoAdvanceInputSchema = z.object({
    paused: z.boolean(),
});
export const markCleanupAsCleanInputSchema = z.object({
    confirmationIdentifier: z.string().min(1, "Digite o Identificador do tenant").max(64),
    acknowledgePending: z.literal(true),
});
export const cleanupEventViewSchema = z.object({
    id: z.number().int().positive(),
    jobId: z.string().uuid().nullable(),
    tenantIdentifier: z.string().min(1).max(64),
    eventType: z.string().min(1).max(64),
    actorId: z.string().uuid().nullable(),
    payload: z.record(z.string(), z.unknown()),
    createdAt: z.string().datetime(),
});
export const pocTimelineEntryViewSchema = pocCycleFieldsSchema.extend({
    actualEndAt: z.string().datetime(),
});
export const pocTenantViewSchema = tenantViewSchema.extend({
    classification: z.literal("POC"),
    status: pocOperationalStatusSchema,
    activeCycle: pocActiveCycleViewSchema.nullable(),
    cleanupJob: cleanupJobViewSchema.nullable(),
});
const nullableTrimmedText = (maximum, message) => z
    .union([z.string().trim().max(maximum, message), z.null()])
    .transform((value) => (value === "" ? null : value));
export const trialStateInputSchema = z
    .object({
    lead: nullableTrimmedText(160, "O Lead deve ter no máximo 160 caracteres"),
    startDate: localDateSchema.nullable(),
    expectedEndDate: localDateSchema.nullable(),
    commercialOwner: nullableTrimmedText(120, "O Comercial responsável deve ter no máximo 120 caracteres"),
    notes: nullableTrimmedText(4_000, "As anotações devem ter no máximo 4.000 caracteres"),
})
    .superRefine((input, context) => {
    if (input.startDate && input.expectedEndDate && input.expectedEndDate < input.startDate) {
        context.addIssue({
            code: "custom",
            path: ["expectedEndDate"],
            message: "A previsão de término não pode anteceder o início",
        });
    }
});
export const trialStateViewSchema = trialStateInputSchema.extend({
    updatedAt: z.string().datetime(),
});
export const trialTenantViewSchema = tenantViewSchema.extend({
    classification: z.literal("TRIAL"),
    state: trialStateViewSchema.nullable(),
});
export const preImplementationTenantListViewSchema = z.object({
    pocs: z.array(pocTenantViewSchema),
    trials: z.array(trialTenantViewSchema),
    cleanupService: cleanupServiceViewSchema,
    eventCursor: z.number().int().nonnegative(),
    synchronizedAt: z.string().datetime(),
    source: tenantSnapshotSourceSchema,
});
export const pocTimelineViewSchema = z.object({
    tenantIdentifier: z.string().min(1).max(64),
    cycles: z.array(pocTimelineEntryViewSchema),
});
export const vpnConnectionStatusSchema = z.enum([
    "UNCONFIGURED",
    "DISCONNECTED",
    "CONNECTING",
    "CONNECTED",
    "ERROR",
]);
export const mysqlConnectionStatusSchema = z.enum(["UNCONFIGURED", "UNAVAILABLE", "AVAILABLE", "ERROR"]);
export const tenantSyncStatusSchema = z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "FAILED"]);
export const tenantSyncTriggerSchema = z.enum(["SCHEDULED", "MANUAL", "CONNECTION_RECOVERY"]);
export const vpnConnectInputSchema = z.object({
    totp: z.string().regex(/^\d{6}$/, "Informe o código temporário de seis dígitos"),
});
export const mysqlConfigurationInputSchema = z.object({
    host: z.string().trim().min(1, "Informe o servidor").max(253),
    port: z.number().int().min(1).max(65_535).default(3306),
    customerDatabase: z.string().trim().min(1).max(64).default("customer"),
    authDatabase: z.string().trim().min(1).max(64).default("auth"),
    username: z.string().trim().min(1, "Informe o usuário").max(128),
    password: z.string().min(1, "Informe a senha").max(512),
});
const dateTimeOrNullSchema = z.string().datetime().nullable();
export const synchronizationRunViewSchema = z.object({
    id: z.string().uuid(),
    trigger: tenantSyncTriggerSchema,
    status: tenantSyncStatusSchema,
    requestedAt: z.string().datetime(),
    startedAt: dateTimeOrNullSchema,
    completedAt: dateTimeOrNullSchema,
    tenantCount: z.number().int().nonnegative().nullable(),
    errorCode: z.string().nullable(),
});
export const adminConnectivityViewSchema = z.object({
    vpn: z.object({
        configured: z.boolean(),
        status: vpnConnectionStatusSchema,
        checkedAt: dateTimeOrNullSchema,
        connectedAt: dateTimeOrNullSchema,
        errorCode: z.string().nullable(),
    }),
    mysql: z.object({
        configured: z.boolean(),
        status: mysqlConnectionStatusSchema,
        checkedAt: dateTimeOrNullSchema,
        lastSucceededAt: dateTimeOrNullSchema,
        errorCode: z.string().nullable(),
        summary: z
            .object({
            host: z.string(),
            port: z.number().int(),
            customerDatabase: z.string(),
            authDatabase: z.string(),
            username: z.string(),
            passwordConfigured: z.literal(true),
        })
            .nullable(),
    }),
    synchronization: z.object({
        latestSnapshotAt: dateTimeOrNullSchema,
        nextScheduledAt: dateTimeOrNullSchema,
        latestRun: synchronizationRunViewSchema.nullable(),
    }),
});
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
    }),
});
export function buildCorporateEmail(prefix) {
    return `${prefix.toLowerCase()}${CORPORATE_EMAIL_SUFFIX}`;
}
export function hasGlobalAccess(roles) {
    return roles.includes("ADMIN");
}
export function hasAnyRole(roles, required) {
    return roles.includes("ADMIN") || required.some((role) => roles.includes(role));
}
//# sourceMappingURL=index.js.map